import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ChevronDown, LayoutDashboard, LogOut, Menu, User, X } from "lucide-react";
import AuthModal from "@/components/auth/AuthModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useToast } from "@/hooks/use-toast";
import { hasStoredAuthSession } from "@/lib/hasStoredAuthSession";
import "./marketing.css";

export type MarketingNavKey = "agents" | "teams" | "pricing";

const NAV: { key: MarketingNavKey; label: string; to: string }[] = [
  { key: "agents", label: "Agents", to: "/agents" },
  { key: "teams", label: "Teams & Brokerages", to: "/teams-brokerages" },
  { key: "pricing", label: "Pricing", to: "/pricing" },
];

type HeaderUser = { email?: string | null; user_metadata?: { full_name?: string } } | null;

function initials(user: HeaderUser): string {
  const full = user?.user_metadata?.full_name;
  if (full) {
    return full
      .split(" ")
      .map((p) => p[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return user?.email?.charAt(0).toUpperCase() || "U";
}

function displayName(user: HeaderUser): string {
  if (!user) return "";
  return user.user_metadata?.full_name || user.email?.split("@")[0] || "User";
}

/**
 * Marketing-site header (v3.3). Sticky 72px bar: logo + muted "Beta" · centred
 * Agents / Teams & Brokerages / Pricing · Sign In + Sign Up Free. Below a
 * 1040px header width the nav collapses behind a 44px menu button; the open
 * sheet lists the routes, then Sign Up Free above Sign In.
 *
 * Agent sign-up is open (founder decision F6, 2026-10-21): Sign Up Free opens
 * AuthModal in sign-up mode and Sign In opens it in sign-in mode, with sign-up
 * reachable from there. Signed-in visitors get the role-aware account menu
 * (dashboardPath / Organization). The modal is owned here as a sibling of the
 * menu so closing the menu never unmounts it.
 */
const MarketingHeader = ({ active }: { active?: MarketingNavKey }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signin");
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const { user, isAuthenticated, signOut, isLoading } = useAuth();
  const { dashboardPath, isAdmin } = useUserRole();
  const { toast } = useToast();
  // With no saved session the auth check can only say "signed out", so show
  // Sign In / Sign Up straight away instead of a placeholder. The prerender
  // pass has no storage, so the static HTML carries the buttons too.
  const [mayHaveSession] = useState(hasStoredAuthSession);
  const showPlaceholder = isLoading && mayHaveSession;

  // Close the sheet on navigation, on Escape (focus back to the toggle), and
  // when the header grows past the breakpoint.
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      toggleRef.current?.focus();
    };
    const wide = window.matchMedia?.("(min-width: 1040px)");
    const onWide = () => wide?.matches && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    wide?.addEventListener?.("change", onWide);
    return () => {
      document.removeEventListener("keydown", onKey);
      wide?.removeEventListener?.("change", onWide);
    };
  }, [menuOpen]);

  // A dialog opened from inside the sheet returns focus to the menu button,
  // because its trigger is gone once the sheet closes.
  const openFromMenu = (open: () => void) => {
    setMenuOpen(false);
    open();
  };
  const openAuth = (mode: "signin" | "signup") => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  const handleSignOut = async () => {
    await signOut();
    setMenuOpen(false);
    toast({ title: "Signed out", description: "You've been successfully signed out." });
  };

  const accountLabel = isAdmin ? "Organization" : "Dashboard";
  const profileLabel = isAdmin ? "Agent Profile" : "My Profile";

  return (
    <div className="mk-header-wrap">
      <header className="mk-header">
        <div className="mk-header__bar">
          <Link to="/" className="mk-header__brand" aria-label="Compaire home">
            <img className="mk-header__logo" src="/compaire-logo.svg" alt="" width={240} height={22} />
            <span className="mk-header__beta">Beta</span>
          </Link>

          <nav className="mk-header__nav" aria-label="Primary">
            {NAV.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                className="mk-header__navlink"
                aria-current={active === item.key ? "page" : undefined}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="mk-header__actions">
            {showPlaceholder ? (
              <span className="mk-header__skeleton" aria-hidden="true" />
            ) : isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="mk-header__user">
                    <span className="mk-avatar" aria-hidden="true">
                      {initials(user)}
                    </span>
                    <span className="mk-header__username">{displayName(user)}</span>
                    <ChevronDown size={16} aria-hidden="true" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border shadow-lg z-[100]">
                  <div className="px-3 py-2 border-b">
                    <p className="text-sm font-medium truncate">{displayName(user)}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link to={dashboardPath} className="flex items-center gap-2 cursor-pointer">
                      <LayoutDashboard className="h-4 w-4" />
                      {accountLabel}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                      <User className="h-4 w-4" />
                      {profileLabel}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <button type="button" className="mk-btn mk-btn--line" onClick={() => openAuth("signin")}>
                  Sign In
                </button>
                <button type="button" className="mk-btn mk-btn--primary" onClick={() => openAuth("signup")}>
                  Sign Up Free
                </button>
              </>
            )}
          </div>

          <button
            ref={toggleRef}
            type="button"
            className="mk-header__toggle"
            aria-expanded={menuOpen}
            aria-controls="mk-mobile-menu"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>

        {menuOpen && (
          <div ref={menuRef} id="mk-mobile-menu" className="mk-menu">
            <nav aria-label="Primary" className="mk-stack">
              {NAV.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  className="mk-menu__link"
                  aria-current={active === item.key ? "page" : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                  <ArrowRight size={20} aria-hidden="true" />
                </Link>
              ))}
            </nav>
            {isAuthenticated ? (
              <div className="mk-stack">
                <div className="mk-menu__account">
                  <span className="mk-avatar" aria-hidden="true">
                    {initials(user)}
                  </span>
                  <span>
                    {displayName(user)}
                    <small>{user?.email}</small>
                  </span>
                </div>
                <Link to={dashboardPath} className="mk-menu__link" onClick={() => setMenuOpen(false)}>
                  {accountLabel}
                  <LayoutDashboard size={20} aria-hidden="true" />
                </Link>
                <Link to="/profile" className="mk-menu__link" onClick={() => setMenuOpen(false)}>
                  {profileLabel}
                  <User size={20} aria-hidden="true" />
                </Link>
                <button type="button" className="mk-menu__link mk-menu__signout" onClick={handleSignOut}>
                  Sign Out
                  <LogOut size={20} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <div className="mk-menu__actions">
                <button
                  type="button"
                  className="mk-btn mk-btn--primary mk-btn--block"
                  onClick={() => openFromMenu(() => openAuth("signup"))}
                >
                  Sign Up Free
                </button>
                <button
                  type="button"
                  className="mk-btn mk-btn--line mk-btn--block"
                  onClick={() => openFromMenu(() => openAuth("signin"))}
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultMode={authMode}
        allowSignup
        appearance="marketing"
      />
    </div>
  );
};

export default MarketingHeader;
